import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { addCardOnCheckoutFlow } from 'legacy/util/modalFlows';
import { types as paymentActionTypes } from 'legacy/actions/paymentActions';
import { useEditMeCommand, useGetMe } from 'legacy/user/hooks';
import { MultiTierCheckoutSession } from 'legacy/events/types';
import { Card } from 'legacy/common/types/models';

type SetSelectedCardFn = (
  cardId: string,
  callback?: (cardId: string) => Promise<MultiTierCheckoutSession>
) => void;

type SetSelectedBuyNowPayLaterFn = (
  buyNowPayLater: 'klarna' | 'affirm',
  callback?: (
    buyNowPayLater: 'klarna' | 'affirm'
  ) => Promise<MultiTierCheckoutSession>
) => void;

/**
 * Hook providing functionality for managing payment methods.
 * It allows the user to add a new credit card, select a card, and retrieve the list of
 * existing cards associated with the user's account.
 */
const usePaymentMethod = (paymentMethodOptions?: { returnUrl?: string }) => {
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [selectedBuyNowPayLater, setSelectedBuyNowPayLater] = useState<
    'klarna' | 'affirm' | null
  >(null);
  const dispatch = useDispatch();
  const { data: user, refetch: refetchUser } = useGetMe();
  const editMeCommand = useEditMeCommand();

  /**
   * Returns the Stripe payment method ID for a given card ID.
   */
  const getCardStripePaymentMethodIdFromCardId = (
    cardId: string,
    cards?: Card[]
  ) => {
    const userCards: Card[] = cards || user?.cards;

    if (userCards) {
      return userCards.find((card) => card._id === cardId)?.stripe_id;
    }

    return undefined;
  };

  /**
   * NOTE: OLD CODE
   * This allows the guest to add a new credit card to their account.
   */
  const addNewCard = (options?: { onSuccess }) => {
    const showEmailField = !user?.email;

    addCardOnCheckoutFlow(
      (cards) => {
        const updatedCardId = cards?.[cards?.length - 1]?._id;

        refetchUser();

        if (options?.onSuccess) {
          options.onSuccess(updatedCardId, cards).then(() => {
            setSelectedBuyNowPayLater(null);
            setSelectedCard(updatedCardId);
          });
        }
      },
      {
        showEmailField,
        setEmail: (email, onSuccess) => {
          dispatch({ type: paymentActionTypes.ADD_CARD.start });
          editMeCommand.mutate(
            { email },
            {
              onSuccess,
              onError: (err) => {
                // Copied from paymentActions
                dispatch({
                  type: paymentActionTypes.STRIPE_CARD.error
                });
                dispatch({
                  type: paymentActionTypes.ADD_CARD_FORM.success,
                  payload: {
                    FORM_ERROR: err.message
                  }
                });
              }
            }
          );
        },
        isSetEmailLoading: editMeCommand.isLoading
      },
      {
        returnUrl: paymentMethodOptions?.returnUrl
      }
    );
  };

  /**
   * Updates the selected card and triggers a checkout update.
   */
  const onSelectCard: SetSelectedCardFn = (cardId, checkoutUpdater) => {
    if (checkoutUpdater) {
      checkoutUpdater(cardId).then(() => {
        setSelectedBuyNowPayLater(null);
        setSelectedCard(cardId);
      });
    } else {
      setSelectedBuyNowPayLater(null);
      setSelectedCard(cardId);
    }
  };

  /**
   * Updates the selected buy now pay later and triggers a checkout update.
   */
  const onSelectBuyNowPayLater: SetSelectedBuyNowPayLaterFn = (
    buyNowPayLaterType,
    checkoutUpdater
  ) => {
    if (checkoutUpdater) {
      checkoutUpdater(buyNowPayLaterType).then(() =>
        setSelectedBuyNowPayLater(buyNowPayLaterType)
      );
    } else {
      setSelectedBuyNowPayLater(buyNowPayLaterType);
    }
  };

  return {
    selectedBuyNowPayLater: {
      value: selectedBuyNowPayLater,
      setSelectedBuyNowPayLater: onSelectBuyNowPayLater
    },
    selectedCard: { value: selectedCard, setSelectedCard: onSelectCard },
    addNewCard,
    cards: user?.cards || [],
    getCardStripePaymentMethodIdFromCardId
  };
};

export default usePaymentMethod;
